// AdvancedRuleFrame.js
import React, { useEffect, useMemo, useState } from "react";
import { useSelector, useDispatch } from "react-redux";
import { Alert, Box, Button, CircularProgress } from "@mui/material";
import NodeEditor from "./NodeEditor";
import SimpleNodeViewer from "./SimpleViewer";
import {
  saveRuleset,
  loadRuleset,
  fetchTaskMetadata,
  GLOBAL_ERROR_KEY,
} from "../reducers/rulesetSlice";
import { findNodeById } from "./nodeUtils";

const AdvancedRuleFrame = () => {
  const { root, nodeConfigs, loading, error, errors, success } = useSelector(
    (state) => state.ruleset,
  );

  const globalErrors = errors?.[GLOBAL_ERROR_KEY];
  const globalError = globalErrors?.rules;
  const outcomeErrors = globalErrors?.outcomes || [];
  const structureErrors = globalErrors?.structure || [];

  const loggedInUser = useSelector((state) => state.authen.loggedInUser);

  const license_key = loggedInUser?.license_key;
  const user_id = loggedInUser?.user_id;
  const machine_uuid = loggedInUser?.machine_uuid;

  const has_write = useSelector(
    (state) => state.authen.loggedInUser?.license_role === "Change",
  );

  const has_advanced = useSelector(
    (state) => state.authen.loggedInUser?.advanced_licenses?.pathology,
  );

  const { selectedCategory } = useSelector((state) => state.ruleFrame);
  const clinicians = useSelector((state) => state.ruleset.clinicians);
  const taskTypes = useSelector((state) => state.ruleset.taskTypes);
  const [selectedPrefix, setSelectedPrefix] = useState([]);
  const dispatch = useDispatch();
  const [selectedNodeId, setSelectedNodeId] = useState(null);

  // Load ruleset on mount (or when category changes)
  useEffect(() => {
    if (license_key && selectedCategory) {
      dispatch(loadRuleset({ license_key, category: selectedCategory }));
      dispatch(
        fetchTaskMetadata({
          user_id,
          license_key,
          machine_uuid,
        }),
      );

      setSelectedNodeId(null);
    }
  }, [dispatch, license_key, user_id, machine_uuid, selectedCategory]);

  useEffect(() => {}, [root]);

  useEffect(() => {}, [clinicians, taskTypes]);

  const handleSave = () => {
    dispatch(
      saveRuleset({ ruleset: root, license_key, category: selectedCategory }),
    );
  };

  const hasErrors = Object.values(errors || {}).some(
    (nodeErr) => nodeErr && Object.keys(nodeErr).length > 0,
  );

  // compute the node to edit when selectedNodeId is set
  const nodeToEdit = useMemo(() => {
    if (!selectedNodeId) return root;
    const found = findNodeById(root, selectedNodeId);
    return found || root;
  }, [root, selectedNodeId]);

  return (
    <Box>
      {/* Viewer (1/3) and Editor (2/3) side by side */}
      <Box sx={{ display: "flex", gap: 2, alignItems: "flex-start" }}>
        <Box sx={{ flex: 1 }}>
          <SimpleNodeViewer
            root={root}
            selectedCategory={selectedCategory}
            nodeConfigs={nodeConfigs}
            onSelectNode={({ id, numbering }) => {
              setSelectedNodeId(id);
              setSelectedPrefix(
                numbering
                  ? numbering.split(".").map((n) => parseInt(n, 10))
                  : [],
              );
            }}
            selectedNodeId={selectedNodeId}
          />
        </Box>

        <Box sx={{ flex: 2 }}>
          <NodeEditor
            node={nodeToEdit}
            selectedCategory={selectedCategory}
            nodeConfigs={nodeConfigs}
            prefix={selectedPrefix}
          />
        </Box>
      </Box>

      {/* Save button and error message below */}
      <Box
        sx={{
          mt: 2,
          textAlign: "right",
          pointerEvents: has_write && has_advanced ? "auto" : "none",
          opacity: has_write && has_advanced ? 1 : 0.2,
        }}
      >
        {globalError && (
          <Alert severity="warning" sx={{ mb: 1, textAlign: "left" }}>
            {globalError}
          </Alert>
        )}
        {outcomeErrors.length > 0 && (
          <Alert severity="error" sx={{ mb: 1, textAlign: "left" }}>
            <strong>Fix these outcome conflicts before saving:</strong>
            <ul style={{ margin: "4px 0 0 16px", padding: 0 }}>
              {outcomeErrors.map((m) => (
                <li key={m}>{m}</li>
              ))}
            </ul>
          </Alert>
        )}
        {structureErrors.length > 0 && (
          <Alert severity="error" sx={{ mb: 1, textAlign: "left" }}>
            <strong>Fix these structural issues before saving:</strong>
            <ul style={{ margin: "4px 0 0 16px", padding: 0 }}>
              {structureErrors.map((m, i) => (
                <li key={`${i}-${m}`}>{m}</li>
              ))}
            </ul>
          </Alert>
        )}
        {success && (
          <Box sx={{ mt: 1 }}>
            <Alert severity="success" sx={{ display: "inline-flex" }}>
              {success}
            </Alert>
          </Box>
        )}
        {error && (
          <Box sx={{ mt: 1 }}>
            <Alert severity="error" sx={{ display: "inline-flex" }}>
              {String(error)}
            </Alert>
          </Box>
        )}

        <Button
          variant="contained"
          color="primary"
          onClick={handleSave}
          disabled={loading || hasErrors}
        >
          {loading ? <CircularProgress size={20} /> : "Save Ruleset"}
        </Button>
      </Box>
    </Box>
  );
};

export default AdvancedRuleFrame;
